/**
 * /academy/challenge for the IELTS track (D-105): 10 course exercises of every form, the same for
 * everyone that day, against the clock; submit → score, XP and the leaderboard of the day.
 */

import {
  ApiError,
  academyChallengeCreate,
  getAcademyChallengeRetrieveQueryKey,
  getAcademyProgressRetrieveQueryKey,
  useAcademyChallengeRetrieve,
} from "@agencyos/api-client";
import { type Item, type ItemResponse, challengeXp, initialResponse, parseChallengeReview, isEmptyResponse, parseItem, parseResponse } from "@agencyos/core";
import { Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ListChecks, Sparkles, Swords, Timer as TimerIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { readStorage, writeStorage } from "../../lib/storage";
import { Leaderboard, useChallengeClock } from "../ChallengePage";
import { ChallengeReview } from "../ChallengeReview";
import { parseEntry } from "../progressTypes";
import { Timer } from "../shared";
import { clock } from "../text";
import { isObject } from "../types";
import { PageHead, Ring } from "../ui";
import { ContentLangProvider } from "./contentLang";
import { type ExamCode, IELTS, contentLanguage } from "./exam";
import { ListenScript } from "./Exercise";
import { ExamPictureView } from "../ExamPicture";
import { ItemInput } from "./ItemInput";

const draftKey = (id: string) => `academy.ielts.challenge.${id}`;

function readDraft(id: string): Record<string, ItemResponse> {
  try {
    const raw: unknown = JSON.parse(readStorage(draftKey(id)) ?? "{}");
    if (!isObject(raw)) return {};
    return Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, parseResponse(v)]));
  } catch {
    return {};
  }
}

/** The daily challenge of the course exercises (IELTS, and the TEF with `exam`, D-109). */
export function IeltsChallengePage({ exam = IELTS }: { exam?: ExamCode } = {}) {
  const { t } = useTranslation();
  const params = useMemo(() => ({ exam }), [exam]);
  const queryClient = useQueryClient();
  const query = useAcademyChallengeRetrieve(params, { query: { retry: false } });
  const view = query.data?.status === 200 ? query.data.data : undefined;
  const entry = useMemo(() => parseEntry(view?.entry), [view]);
  const items = useMemo(
    () => (Array.isArray(view?.questions) ? view.questions.map(parseItem).filter((i): i is Item => i !== null) : []),
    [view],
  );
  const [answers, setAnswers] = useState<Record<string, ItemResponse>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitted = useRef(false);
  const running = Boolean(entry && !entry.finished);
  const left = useChallengeClock(entry?.startedAt ?? "", view?.minutes ?? 10, running);

  useEffect(() => {
    if (view?.id) setAnswers(readDraft(view.id));
  }, [view?.id]);

  const act = useCallback(
    async (action: "start" | "submit", sent?: Record<string, ItemResponse>) => {
      setBusy(true);
      setError(null);
      try {
        const response = await academyChallengeCreate(action === "submit" ? { action, answers: sent ?? {}, exam } : { action, exam });
        queryClient.setQueryData(getAcademyChallengeRetrieveQueryKey(params), response);
        if (action === "submit") {
          writeStorage(draftKey(response.data.id), null);
          void queryClient.invalidateQueries({ queryKey: getAcademyProgressRetrieveQueryKey(params) });
        }
      } catch (failure) {
        setError(failure instanceof ApiError && failure.detail ? failure.detail : t("common.error"));
        if (failure instanceof ApiError && failure.status === 400) void query.refetch();
      } finally {
        setBusy(false);
      }
    },
    [queryClient, query, t, exam, params],
  );

  useEffect(() => {
    if (running && left === 0 && !submitted.current) {
      submitted.current = true;
      void act("submit", answers);
    }
  }, [running, left, act, answers]);

  if (query.isLoading) return <Skeleton style={{ height: 280 }} />;
  if (query.error instanceof ApiError && query.error.status === 404) {
    return (
      <div className="ac-page">
        <div className="ac-surface ac-card ac-card--center ac-empty">
          <span className="ac-challenge-hero__icon" aria-hidden="true">
            <Swords size={26} />
          </span>
          <h1 className="ac-title-2">{t("academy.challenge.title")}</h1>
          <p className="ac-secondary">{t("academy.challenge.none")}</p>
        </div>
      </div>
    );
  }
  if (!view) {
    return (
      <GlassCard className="ac-card ac-card--center">
        <p className="ko">{t("common.error")}</p>
        <Button variant="secondary" onClick={() => void query.refetch()}>
          {t("common.retry")}
        </Button>
      </GlassCard>
    );
  }

  const set = (id: string, value: ItemResponse) => {
    const next = { ...answers, [id]: value };
    setAnswers(next);
    writeStorage(draftKey(view.id), JSON.stringify(next));
  };
  const total = entry?.total || items.length || 10;
  const answeredCount = items.filter((i) => !isEmptyResponse(i, answers[i.id] ?? null)).length;
  const xpWon = entry?.finished ? challengeXp(view.xp, entry.score, entry.total) : 0;

  // D-123: the TEF and German items keep their language (French, German): they were marked English
  const lang = contentLanguage(exam);
  return (
    <ContentLangProvider lang={lang}>
    <div className="ac-page">
      <PageHead
        eyebrow={`${t(`ielts.exams.${exam}.name`)} · ${t("academy.challenge.eyebrow")}`}
        title={t("academy.challenge.title")}
        subtitle={<p>{t("ielts.challenge.rules", { count: total, minutes: view.minutes, xp: view.xp })}</p>}
      />

      {!entry && (
        <section className="ac-challenge-hero" aria-labelledby="ielts-challenge-start">
          <div className="ac-challenge-hero__icon" aria-hidden="true">
            <Swords size={28} />
          </div>
          <div className="ac-challenge-hero__text">
            <h2 id="ielts-challenge-start" className="ac-title-2">
              {t("academy.challenge.ready")}
            </h2>
            <p className="ac-secondary">{t(exam === IELTS ? "ielts.challenge.startHint" : exam === "tef_canada" ? "tef.challenge.startHint" : "goethe.challenge.startHint")}</p>
            <ul className="ac-challenge-hero__facts">
              <li>
                <ListChecks aria-hidden size={15} /> {t("ielts.challenge.exercises", { count: total })}
              </li>
              <li>
                <TimerIcon aria-hidden size={15} /> {t("academy.mocks.minutes", { count: view.minutes })}
              </li>
              <li>
                <Sparkles aria-hidden size={15} /> {t("academy.ui.challenge.upTo", { xp: view.xp })}
              </li>
            </ul>
          </div>
          <Button size="lg" onClick={() => void act("start")} disabled={busy}>
            {busy ? t("common.loading") : t("academy.challenge.start")}
          </Button>
        </section>
      )}

      {entry && !entry.finished && (
        <section className="ac-challenge-run" aria-labelledby="ielts-challenge-q">
          <form
            className="ac-challenge-run__form"
            onSubmit={(event) => {
              event.preventDefault();
              submitted.current = true;
              void act("submit", answers);
            }}
          >
            <div className="ac-challenge-run__bar">
              <h2 id="ielts-challenge-q" className="ac-headline ao-num">
                {t("academy.challenge.answered", { answered: answeredCount, total: items.length })}
              </h2>
              <div className="ac-progress" aria-hidden="true">
                <span style={{ transform: `scaleX(${items.length ? answeredCount / items.length : 0})` }} />
              </div>
              <Timer seconds={left} label={t("academy.player.timeLeft")} />
            </div>
            <ol className="ac-challenge-list">
              {items.map((item, index) => (
                <li key={item.id} className="ac-question__sheet ac-item">
                  <p className="ac-player__eyebrow ao-num">{t("academy.player.progress", { n: index + 1, total: items.length })}</p>
                  {item.text && (
                    <div className="ac-document ac-document--small" lang={lang}>
                      {item.text}
                    </div>
                  )}
                  {item.script && <ListenScript script={item.script} />}
                  <ExamPictureView picture={item.image} lang={lang} size="medium" />
                  {item.type !== "gap" && (
                    <h3 id={`ch-${item.id}`} className="ac-stem ac-stem--small" lang={lang}>
                      {item.prompt}
                    </h3>
                  )}
                  <ItemInput item={item} value={answers[item.id] ?? initialResponse(item)} onChange={(value) => set(item.id, value)} labelledBy={`ch-${item.id}`} />
                </li>
              ))}
            </ol>
            <div className="ac-question__actions">
              <Button type="submit" size="lg" disabled={busy}>
                {busy ? t("common.loading") : t("academy.challenge.submit")}
              </Button>
            </div>
          </form>
        </section>
      )}

      {error && (
        <p className="ko" role="alert">
          {error}
        </p>
      )}

      {entry?.finished ? (
        <div className="ac-grid ac-challenge-result">
          <section className="ac-challenge-done ac-span-5" aria-labelledby="ielts-challenge-score">
            <Ring ratio={entry.total ? entry.score / entry.total : 0} size={148} stroke={12} tone="warning">
              <span className="ac-challenge-done__score ao-num" role="status">
                {t("academy.challenge.score", { score: entry.score, total: entry.total })}
              </span>
            </Ring>
            <div className="ac-challenge-done__text">
              <h2 id="ielts-challenge-score" className="ac-title-2">
                {t("academy.challenge.done")}
              </h2>
              <ul className="ac-challenge-hero__facts">
                <li>
                  <TimerIcon aria-hidden size={15} /> {t("academy.challenge.time", { time: clock(entry.seconds) })}
                </li>
                <li className="ac-xp">
                  <Sparkles aria-hidden size={15} /> {t("academy.challenge.xp", { count: xpWon })}
                </li>
              </ul>
              <p className="ac-footnote">{t("academy.challenge.comeBack")}</p>
            </div>
          </section>
          <div className="ac-span-7">
            <Leaderboard view={view} total={total} />
          </div>
        </div>
      ) : null}
      {entry?.finished && <ChallengeReview rows={parseChallengeReview(view.review)} lang={lang} />}
      {!entry && <Leaderboard view={view} total={total} />}
    </div>
    </ContentLangProvider>
  );
}
